import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { Globals } from '../../globals';
import { AuthService } from '../../security/auth.service';
import { ClientVersionService } from '../client-version/client-version.service';
import {
  Message_v2,
  MessagesCollection,
} from './messagescollection.interfaces';

@Injectable({
  providedIn: 'root',
})
export class MessagesCollectionService {
  private readonly http = inject(HttpClient);
  private readonly globals = inject(Globals);
  private readonly clientVersionService = inject(ClientVersionService);
  private readonly authService = inject(AuthService);

  /**
   * Gets all message collections and transforms them into the old structure.
   */
  getAllMessagesCollections(): Observable<MessagesCollection[]> {
    const clientVersion = this.clientVersionService.getVersion();
    const url = `${this.globals.apiRoot}/jwtmessagescollection?clientVersion=${clientVersion}&isApp=true`;

    return this.http
      .get<{ MessagesCollections: MessagesCollection[] }>(url)
      .pipe(
        map((response) => {
          const collections = response.MessagesCollections || [];

          return collections.map((collection) => {
            const transformedMessages = (collection.messages || []).map(
              (message) => {
                const translation = message.translations?.[0] || {
                  messageId: message.id,
                  languageId: this.authService.currentLanguageId(),
                  overviewTitle: '',
                  overviewText: '',
                  text: '',
                };

                return {
                  id: message.id,
                  overviewImage: message.overviewImage,
                  overviewVideo: message.overviewVideo,
                  messagesCollectionId: message.messagesCollectionId,
                  sortOrder: message.sortOrder,
                  translation,
                } as Message_v2;
              },
            );

            return {
              id: collection.id,
              name: collection.name,
              createdAt: new Date(collection.createdAt),
              showFromDateTime: collection.showFromDateTime
                ? new Date(collection.showFromDateTime)
                : undefined,
              showUntilDateTime: collection.showUntilDateTime
                ? new Date(collection.showUntilDateTime)
                : undefined,
              tourId: collection.tourId ?? undefined,
              important: collection.important,
              importantDays: collection.importantDays,
              read: collection.read ?? false,
              readImportant: collection.readImportant,
              priority: collection.priority,
              userFilterId: collection.userFilterId,
              appVersionFrom: collection.appVersionFrom,
              appVersionTo: collection.appVersionTo,
              messages: transformedMessages,
            } as MessagesCollection;
          });
        }),
        catchError((error) => {
          return of([]);
        }),
      );
  }

  /**
   * Gets important message collections.
   */
  getImportantMessagesCollections(): Observable<MessagesCollection[]> {
    const clientVersion = this.clientVersionService.getVersion();
    const url = `${this.globals.apiRoot}/jwtmessagescollection/important?clientVersion=${clientVersion}&isApp=true`;
    return this.http.get<MessagesCollection[]>(url);
  }

  /**
   * Gets the number of unread message collections.
   */
  getUnreadMessagesCollectionsCount(): Observable<number> {
    const clientVersion = this.clientVersionService.getVersion();
    const url = `${this.globals.apiRoot}/jwtmessagescollection/count?clientVersion=${clientVersion}&isApp=true`;
    return this.http.get<number>(url);
  }

  /**
   * Mark an important message inside a collection as read.
   */
  updateImportantMessagesCollection(id: number): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtmessagescollection/important/${id}`;
    return this.http.put<void>(url, id);
  }

  /**
   * Mark a message inside a collection as read.
   */
  updateMessagesCollection(id: number): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtmessagescollection/${id}`;
    return this.http.put<void>(url, {});
  }
}
